/**
 * Attempts to accurately divide to potential decimals.
 * @param {Number} a the first decimal number
 * @param {Number} b the second decimal number
 * @returns the quotient of both numbers, properly divided.
 */
exports.divide = function (a, b) {
  var commonMultiplier = 1000000;

  a *= commonMultiplier;
  b *= commonMultiplier;

  return a / b;
};

/**
 * Accurately multiplies to potential decimals.
 * @param {Number} a the first decimal number
 * @param {Number} b the second decimal number
 * @returns the product of both numbers, properly multiplied.
 */
exports.multiply = function (a, b) {
  var commonMultiplier = 1000000;

  a *= commonMultiplier;
  b *= commonMultiplier;

  return (a * b) / (commonMultiplier * commonMultiplier);
};

/**
 * Rounds a number accurately to n decimal places.
 *
 * @param {Number} value the value to round
 * @param {Number} precision the precision to round to
 * @returns {Number} the rounded value
 */
exports.round = function (value, precision = 2) {
  if (typeof value !== 'number') return 0;

  const str = String(value); // '99.999'
  const [integer, decimal] = str.split('.'); // ['99', '999']
  const decimalLength = decimal ? decimal.length : 0; // 3

  if (decimalLength <= precision) {
    return value;
  }

  const digits = decimal.split(''); // ['9', '9', '9']
  const keeping = digits.splice(0, precision); // ['9', '9']

  const shouldRoundUp = (digitArray) => {
    if (digitArray.length === 0) return false;

    const first = digitArray[0]; // '9'

    return first >= '5'; // true
  };

  const roundUp = (digitArray) => {
    if (digitArray.length === 1) {
      const digit = digitArray[0];

      if (digit < '9') return { digits: [String(Number(digit) + 1)], carry: 0 };
      return { digits: ['0'], carry: 1 };
    }

    const [first, ...rest] = digitArray;

    const { digits, carry } = roundUp(rest);
    const rounded = Number(first) + carry;

    if (rounded < 10) digits.unshift(String(rounded));
    else digits.unshift('0');

    return { digits, carry: rounded === 10 ? 1 : 0 };
  };

  if (shouldRoundUp(digits)) {
    const { digits, carry } = roundUp(keeping);

    if (carry === 1) {
      if (integer.length === 0) return 1;
      if (['0', '-', '-0'].includes(integer)) return 1;

      const intDigits = integer.split('');
      let negative = '';
      if (intDigits[0] === '-') {
        negative = intDigits.shift();
      }

      const { digits: newIntDigits, carry: newCarry } = roundUp(intDigits);

      return Number(
        `${negative}${newCarry}${newIntDigits.join('')}.${digits.join('')}`
      );
    }

    return Number(`${integer}.${digits.join('')}`);
  }

  return Number(`${integer}.${keeping.join('')}`);
};

/**
 * Returns a number as a string with n decimal places.
 * @param {Number} value the value to round
 * @param {Number} precision the precision to round to
 * @returns {String} the rounded value
 */
exports.fixed = function (value, precision = 2) {
  return exports.round(value, precision).toFixed(precision);
};
